import React, {FC, useState} from "react"
import moment from "moment"
import Papa from "papaparse"

import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import theme from "@irati/ui/src/global/theme"
import Flex from "@irati/ui/src/Flex"
import userService from "@irati/identity/services/users"
import {User, UserDateOfBirth} from "@irati/types"
import authentication from "@irati/auth"
import makeCSV from "@irati/utils/csv/makeCSV"
import Button from "@irati/ui/src/Button"
import {Body} from "@irati/ui/src/Typography"

import IntegrationDisclaimerModal from "./IntegrationDisclaimerModal"

interface Props extends InjectIntl {
  hasIntegration: boolean
}

const DownloadCsvTemplate: FC<Props> = ({hasIntegration, formatMessage}) => {
  const [load, setLoad] = useState<Promise<any> | null>(null)
  const [downloadData, setDownloadData] = useState<boolean>()
  const [showDisclaimer, setShowDisclaimer] = useState(false)

  const handleOnDownload = () => {
    if (hasIntegration) {
      setDownloadData(true)
      setShowDisclaimer(true)
    } else {
      downloadUsersData()
    }
  }

  const downloadUsersData = () => {
    const loaded = getUserList().then((users: User[]) => {
      const headers: string[] = [
        formatMessage({id: "admin.components.addPeopleCSV.fieldsMapping.firstName.label"}),
        formatMessage({id: "admin.components.addPeopleCSV.fieldsMapping.lastName.label"}),
        formatMessage({id: "admin.components.addPeopleCSV.fieldsMapping.email.label"}),
        formatMessage({id: "admin.components.addPeopleCSV.fieldsMapping.dateOfBirth.label"}),
        formatMessage({id: "admin.components.addPeopleCSV.fieldsMapping.employmentStartDate.label"}),
        formatMessage({id: "admin.components.addPeopleCSV.fieldsMapping.externalId.label"})
      ]

      const data = users.map((user: User) => [
        user?.firstName,
        user?.lastName,
        user?.email,
        getUserDate(user?.dateOfBirth),
        user?.externalStartDate ? moment(user?.externalStartDate).format("DD/MM/YYYY") : "",
        user?.externalUserId
      ])

      const csv = Papa.unparse({fields: headers, data})
      makeCSV(csv, formatMessage({id: "admin.components.addPeopleCSV.downloadUpdateCsvTemplate.fileName"}))
    })
    setLoad(loaded)
  }

  const handleOnEmptyDownload = () => {
    if (hasIntegration) {
      setDownloadData(false)
      setShowDisclaimer(true)
    } else {
      downloadEmptyTemplate()
    }
  }

  const downloadEmptyTemplate = () => {
    const headers: string[] = [
      formatMessage({id: "admin.components.addPeopleCSV.fieldsMapping.firstName.label"}),
      formatMessage({id: "admin.components.addPeopleCSV.fieldsMapping.lastName.label"}),
      formatMessage({id: "admin.components.addPeopleCSV.fieldsMapping.email.label"}),
      formatMessage({id: "admin.components.addPeopleCSV.fieldsMapping.dateOfBirth.label"}),
      formatMessage({id: "admin.components.addPeopleCSV.fieldsMapping.employmentStartDate.label"}),
      formatMessage({id: "admin.components.addPeopleCSV.fieldsMapping.externalId.label"})
    ]

    const csv = Papa.unparse({fields: headers, data: []})
    makeCSV(csv, formatMessage({id: "admin.components.addPeopleCSV.downloadUpdateCsvTemplate.fileName"}))
  }

  const getUserDate = (date?: UserDateOfBirth) => {
    if (date?.day && date?.month) {
      return date?.year
        ? moment([date.year, date.month - 1, date.day]).format("DD/MM/YYYY")
        : moment({month: date.month - 1, day: date.day}).format("DD/MM")
    }
    return ""
  }

  const getUserList = (users: User[] = [], cursor?: string): Promise<User[]> => {
    return userService
      .getUsersForBusiness({businessId: authentication.business().uuid, cursor}, {fetchPolicy: "network-only"})
      .then(({results, cursor: newCursor}) => {
        const allUsers = [...users, ...results]
        if (newCursor) {
          return getUserList(allUsers, newCursor)
        }
        return allUsers
      })
  }

  return (
    <>
      <Flex>
        <Button cta large onClick={handleOnDownload} load={load}>
          <Flex alignItems="center">
            <Icon icon={IconTypes.fileDownload} color={theme.colors.perkbox.blue} margin="0 5px 0 0" />
            <FormattedMessage id="admin.components.addPeopleCSV.downloadUpdateCsvTemplate.text" />
          </Flex>
        </Button>
        <Body marginLeft marginRight>
          <FormattedMessage id="admin.components.updatePeopleCSV.downloadEmptyTemplateText" />
        </Body>
        <Button cta large onClick={handleOnEmptyDownload}>
          <Flex alignItems="center">
            <Icon icon={IconTypes.fileDownload} color={theme.colors.perkbox.blue} margin="0 5px 0 0" />
            <FormattedMessage id="admin.components.updatePeopleCSV.downloadEmptyTemplate" />
          </Flex>
        </Button>
      </Flex>
      <IntegrationDisclaimerModal
        btnText={
          downloadData
            ? "admin.components.addPeopleCSV.downloadUpdateCsvTemplate.text"
            : "admin.components.updatePeopleCSV.downloadEmptyTemplate"
        }
        show={showDisclaimer}
        onHide={() => setShowDisclaimer(false)}
        onDownload={downloadData ? downloadUsersData : downloadEmptyTemplate}
      />
    </>
  )
}

export default injectIntl(DownloadCsvTemplate)
